import { useEffect, useState } from "react";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { cn } from "../../utils/bem";
import { TestReportModal } from "./TestReportModal";
import {
  getTestConfig,
  loadTestConfig,
  openTestReport,
  subscribeOpenTestReport,
  subscribeTestConfig,
} from "./testReportStore";
import "./TestReportModal.prefix.css";

/**
 * Mounted once in the signed-in shell. Owns the report modal and the ways
 * to open it: the floating "Report" button (when the programme is on and
 * the setting allows it), the Alt+Shift+R hotkey, and the global
 * ``openTestReport()`` used by the user menu. Renders nothing when the
 * programme is off — retiring it in Platform → Testing removes every trace.
 */
export const TestReportLauncher = ({ shifted = false }) => {
  const api = useAPI();
  const { user } = useAuth();
  const [config, setConfig] = useState(getTestConfig());
  const [open, setOpen] = useState(null); // null | preset object

  useEffect(() => {
    if (!user?.id) return;
    loadTestConfig(api).then(setConfig);
    return subscribeTestConfig(setConfig);
  }, [api, user?.id]);

  useEffect(() => subscribeOpenTestReport((preset) => setOpen(preset || {})), []);

  useEffect(() => {
    if (!config?.enabled) return;
    const onKey = (e) => {
      if (e.altKey && e.shiftKey && (e.key === "R" || e.key === "r" || e.code === "KeyR")) {
        e.preventDefault();
        openTestReport();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [config?.enabled]);

  if (!user?.id || !config?.enabled) return null;

  return (
    <>
      {config.show_floating_button && !open && (
        <button
          type="button"
          className={cn("test-report-fab").mod({ shifted }).toClassName()}
          onClick={() => openTestReport()}
          title="Report a test step (Alt+Shift+R)"
          aria-label="Report a test step"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4 4h16v11H8l-4 4z" />
            <path d="M12 7v4" />
            <path d="M12 13.5h.01" />
          </svg>
          <span>Report</span>
        </button>
      )}
      {open && <TestReportModal config={config} preset={open} onClose={() => setOpen(null)} />}
    </>
  );
};
